<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Skeleton from '@/components/Skeleton.vue'
import UIKit from '@pocketprep/ui-kit'
import type { Teach } from '@pocketprep/types'
import { instructorModule } from '@/store/instructor/module'
import { userModule } from '@/store/user/module'
import { isSupportWindow } from '@/utils'

const {
    ModalContainer,
    Input: PocketInput,
    Button: PocketButton,
} = UIKit

const route = useRoute()
const router = useRouter()

const isLoading = ref(false)
const orgName = ref('')
const role = ref<'admin' | 'instructor'>('instructor')
const firstName = ref('')
const lastName = ref('')
const email = ref('')
const isAMTA = ref(false)

const currentUserPendingOrgInstructor = computed(() => instructorModule.getters.getCurrentUserPendingOrgInstructor())

const user = computed(() => userModule.state.user)

const showIntercom = () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('show')
    }
}

const clickJoin = async () => {
    if (!currentUserPendingOrgInstructor.value) {
        return
    }

    isLoading.value = true

    await userModule.actions.updateUser({
        firstName: firstName.value,
        lastName: lastName.value,
    })

    await instructorModule.actions.activateInstructor({
        instructorId: currentUserPendingOrgInstructor.value.objectId,
        organizationId: currentUserPendingOrgInstructor.value.organization.objectId,
    })

    await router.push({
        name: 'stats',
    })
}

onMounted(async () => {
    isLoading.value = true

    // Check for AMTA
    if (route.query?.partner === 'amta') {
        isAMTA.value = true
    }

    let isNoUserError = false

    // If code and email is passed, try to sign in using email + magic code query params
    if (
        route.query
        && typeof route.query.code === 'string'
        && typeof route.query.email === 'string'
    ) {
        // if user is already signed in, sign them out
        if (user.value) {
            await userModule.actions.signOut()
        }

        const code = route.query.code
        const queryEmail = route.query.email
        if (queryEmail && code) {
            try {
                await userModule.actions.signInWithCode({
                    email: queryEmail,
                    code,
                })
            } catch (e) {
                // if you get this error inside invite flow your invite has been cancelled
                if (e instanceof Error && e.message.includes('Unable to find user')) {
                    isNoUserError = true
                }
            }
        }
    }

    // If user still is not signed in, go to /sign-in
    if (!user.value) {
        router.push({
            name: 'sign-in',
            ...(isNoUserError
                ? { query: {
                    error: isNoUserError ? 'This invite link is no longer valid.' : '',
                } }
                : {}),
        })
        return
    }

    // Now that we have an authenticated user, fetch the user's org instructors
    await instructorModule.actions.fetchCurrentUserOrgInstructors()

    if (!currentUserPendingOrgInstructor.value) {
        router.push({
            name: 'admin',
        })
        return
    }
    orgName.value = (currentUserPendingOrgInstructor.value.organization as Teach.Class.OrganizationJSON).name
    role.value = currentUserPendingOrgInstructor.value.isAdmin ? 'admin' : 'instructor'
    firstName.value = user.value.firstName || ''
    lastName.value = user.value.lastName || ''
    email.value = user.value.username

    isLoading.value = false
})
</script>

<template>
    <Skeleton class="invite">
        <Portal to="modal">
            <ModalContainer key="inviteModal">
                <div
                    class="invite__modal-content"
                    :class="{
                        'invite__modal-content--amta': isAMTA,
                    }"
                >
                    <template v-if="isAMTA">
                        <img
                            class="invite__blob-left"
                            src="@/assets/images/join-blob-left-amta.svg"
                        >
                        <img
                            class="invite__blobs-right"
                            src="@/assets/images/join-blobs-right-amta.svg"
                        >
                    </template>
                    <template v-else>
                        <img
                            class="invite__blob-left"
                            src="@/assets/images/join-blob-left.svg"
                        >
                        <img
                            class="invite__blobs-right"
                            src="@/assets/images/join-blobs-right.svg"
                        >
                    </template>
                    <div class="invite__pocket-prep">
                        <template v-if="isAMTA">
                            <img
                                class="invite__amta-logo"
                                src="@/assets/images/amtalogo-horiz.png"
                            >
                            <div class="invite__logo-divider" />
                        </template>
                        <img
                            class="invite__pocket-prep-icon"
                            src="@/assets/images/pocket-prep-icon.svg"
                        >
                        Pocket Prep
                    </div>
                    <div class="invite__join-org">
                        Join {{ orgName }}
                    </div>
                    <div class="invite__role-container">
                        as an
                        <div class="invite__role-highlight">
                            <img
                                v-if="role === 'admin'"
                                class="invite__highlight-admin"
                                src="@/assets/images/highlight-short.svg"
                            >
                            <img
                                v-else-if="role === 'instructor'"
                                class="invite__highlight-instructor"
                                src="@/assets/images/highlight-long.svg"
                            >
                            <div class="invite__role">
                                {{ role }}
                            </div>
                        </div>
                    </div>
                    <PocketInput
                        v-model="firstName"
                        class="invite__first-name"
                        field-type="text"
                        label="First Name"
                    />
                    <PocketInput
                        v-model="lastName"
                        class="invite__last-name"
                        field-type="text"
                        label="Last Name"
                    />
                    <PocketInput
                        v-model="email"
                        class="invite__email"
                        field-type="email"
                        label="Email"
                        disabled
                    />
                    <PocketButton
                        class="invite__join-btn"
                        :disabled="isLoading || !firstName || !lastName"
                        @click="clickJoin"
                    >
                        Join {{ orgName }}
                    </PocketButton>
                    <PocketButton
                        class="invite__help-link"
                        type="tertiary-small"
                        @click="showIntercom"
                        @keydown.enter="showIntercom"
                    >
                        I need help
                    </PocketButton>
                </div>
            </ModalContainer>
        </Portal>
    </Skeleton>
</template>

<style lang="scss" scoped>


.invite {
    &__modal-content {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 966px;
        height: 584px;
        background-color: $floral-white;
        padding-top: 42px;
        box-sizing: border-box;

        &--amta {
            background-color: rgba(#ed5025, 0.05);
        }
    }

    &__blob-left {
        position: absolute;
        top: 0;
        left: 0;
    }

    &__blobs-right {
        position: absolute;
        right: 0;
        bottom: 0;
    }

    &__amta-logo {
        width: 85px;
    }

    &__logo-divider {
        height: 30px;
        width: 1px;
        background-color: #f0cdc4;
        margin: 0 22px;
    }

    &__pocket-prep {
        display: flex;
        align-items: center;
        font-weight: 600;
        margin-bottom: 40px;
    }

    &__pocket-prep-icon {
        width: 26px;
        height: 26px;
        margin-right: 6px;
    }

    &__join-org {
        font-size: 36px;
        line-height: 46px;
        font-weight: 600;
        margin-bottom: 6px;
    }

    &__role-container {
        display: flex;
        font-size: 20px;
        line-height: 25px;
        margin-bottom: 42px;
    }

    &__role-highlight {
        position: relative;
        z-index: 1;
    }

    &__role {
        position: relative;
        z-index: 2;
        padding-left: 6px;
    }

    &__highlight-admin,
    &__highlight-instructor {
        position: absolute;
        top: 4px;
    }

    &__first-name,
    &__last-name {
        margin-bottom: 19px;
        width: 320px;
    }

    &__email {
        margin-bottom: 30px;
        width: 320px;
    }

    &__help-link {
        position: absolute;
        bottom: 16px;
    }
}
</style>
